/* eslint-disable max-lines, unicorn/filename-case -- TODO fix later */
import { clientSDK, queryClient } from "@istari-frontend/api-client";
import {
  AccessRelationship,
  ArchiveStatusName,
  Artifact,
  ResourceType as ClientResourceType,
  FileRevision,
  IstariFile,
  Model as IstariModel,
  NewCredentialBinding,
  NewJob,
  OS,
  PageArtifact,
  PageResourceSearchItem,
  ResourceSearchItem,
  Source,
  UpstreamRemoteInfo,
} from "@istari/istari-client";
import { keepPreviousData, queryOptions, useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { DEFAULT_CACHE_TIME, QUERY_STALE_TIME_MS_30S } from "~/constants";
import { AccessActionType, HttpStatusCodeType, ResourceType } from "~/enums";
import { adaptiveJobPageRefetchInterval } from "~/jobs/data-source/job-polling";
import { fileRevisionQueryOptions } from "~/resource/data-source/api/file-revision-query-options";
import { OrgUser } from "~/types/orgUser";
import { userQueryOptions } from "~/user/data-source/user-query-options";
import { userToInternalUser } from "~/user/data-source/user-to-internal-user";
import { resolveUserDisplayNameOrUnknown, UNKNOWN_USER_DISPLAY_NAME } from "~/user/utils/format-user-name";
import { getUserName, sortDateArrayByKeyAndOrder } from "~/utils";
import { downloadBlob } from "~/utils/download-blob";
import { getConnectedFileExtension, getFileNameAndExtension, stripExtensionFromDisplayName } from "~/utils/file";

import { FileProperties } from "./file-properties";
import { getModelOptions } from "./model/getModelOptions";
import { ListOptions, modelQueryKeys, TaskListOptions } from "./model/modelQueryKeys";
import { LabeledControlTag } from "./useControlTags";
import { infosecLevelsKeys } from "./useInfosecLevels";

export interface Model {
  artifacts: [];
  file: {
    revisions: FileRevision[];
    id: string;
    version?: string | undefined;
  };
  id: string;
}

export type ModelProperties = {
  displayFileName?: string | undefined;
  extension: string | undefined;
};

export type FileRevisionWithArtifactId = FileRevision & {
  artifactId?: string | undefined;
  displayFileName?: string | undefined;
};

export const artifactQueryKeys = {
  all: ["artifacts"],
  item: (id: string) => [...artifactQueryKeys.all, id],
  list: (filter: ListOptions) => [...artifactQueryKeys.all, "list", filter],
  artifactContent: (artifactId: string) => [...artifactQueryKeys.item(artifactId), "content"],
  revisionContent: (artifactId: string, revisionId: string) => [
    ...artifactQueryKeys.item(artifactId),
    "revisions",
    revisionId,
    "content",
  ],
  revision: (artifactId: string, revisionId: string) => [
    ...artifactQueryKeys.item(artifactId),
    "revisions",
    revisionId,
  ],
  accessList: (artifactId: string) => [...artifactQueryKeys.item(artifactId), "accessList"],
};

export const resourceAccessQueryKeys = {
  all: ["resource-access"],
  item: (id: string) => [...resourceAccessQueryKeys.all, id],
  accessList: (resourceId: string) => [...resourceAccessQueryKeys.item(resourceId), "accessList"],
};

/**
 * Get specific model revision with properties - caches raw data
 * Note: This reuses the cached model from getModelOptions, or fetches it if not cached
 */
export const getModelRevisionOptions = (modelId: string, revisionId?: string) =>
  queryOptions({
    queryKey: modelQueryKeys.revision(modelId, revisionId || "current"),
    queryFn: async () => {
      const model = await queryClient.ensureQueryData(getModelOptions(modelId));
      const revision = revisionId ? model.file.revisions.find((rev) => rev.id === revisionId) : model.currentRevision();

      if (!revision) {
        throw new Error(`Revision ${revisionId} not found for model ${modelId}`);
      }

      const properties = getFileRevisionPropertiesByRevision(revision);

      // Return raw data structure from API
      return {
        revision,
        properties,
        modelId,
      };
    },
    enabled: !!modelId,
    staleTime: DEFAULT_CACHE_TIME,
  });

/**
 * Get base artifact object with all metadata and revisions
 */
export const getArtifactOptions = (artifactId: string) =>
  queryOptions({
    queryKey: artifactQueryKeys.item(artifactId),
    queryFn: async () => {
      const artifact = await clientSDK.getArtifact(artifactId);
      return artifact;
    },
    enabled: !!artifactId,
    staleTime: DEFAULT_CACHE_TIME,
  });

/**
 * Get specific artifact revision properties - caches raw data
 * Note: This reuses the cached artifact from getArtifactOptions, or fetches it if not cached
 */
export const getArtifactRevisionOptions = (artifactId: string, revisionId?: string) =>
  queryOptions({
    queryKey: artifactQueryKeys.revision(artifactId, revisionId || "current"),
    queryFn: async () => {
      const artifact = await queryClient.ensureQueryData(getArtifactOptions(artifactId));
      const revision = revisionId
        ? artifact.file.revisions.find((rev) => rev.id === revisionId)
        : artifact.currentRevision();

      if (!revision) {
        throw new Error(`Revision ${revisionId} not found for artifact ${artifactId}`);
      }

      const properties = getFileRevisionPropertiesByRevision(revision);

      // Return raw data structure from API
      return {
        revision,
        properties,
        artifact,
        artifactId,
      };
    },
    enabled: !!artifactId,
    staleTime: DEFAULT_CACHE_TIME,
  });

/**
 * Get artifact revision content - caches raw content data
 * Note: This reuses the cached artifact from getArtifactOptions, or fetches it if not cached
 */
export const getArtifactRevisionContentOptions = (artifactId: string, revisionId?: string) =>
  queryOptions({
    queryKey: artifactQueryKeys.revisionContent(artifactId, revisionId || "current"),
    queryFn: async () => {
      const artifact = await queryClient.ensureQueryData(getArtifactOptions(artifactId));
      const revision = revisionId
        ? artifact.file.revisions.find((rev) => rev.id === revisionId)
        : artifact.currentRevision();

      if (!revision) {
        throw new Error(`Revision ${revisionId} not found for artifact ${artifactId}`);
      }

      const content = await revision.readContent();

      // Return raw content data from API
      return {
        content,
        revision,
        artifact,
        artifactId,
      };
    },
    enabled: !!artifactId,
    staleTime: DEFAULT_CACHE_TIME,
  });

// Selector functions for data transformation

/**
 * Selector for artifact revision content - transforms raw data to component shape
 */
export const selectArtifactRevisionContent = (
  data:
    | {
        content: Uint8Array;
        revision: FileRevision;
        artifact: Artifact;
        artifactId: string;
      }
    | undefined,
) => {
  if (!data) return undefined;
  const { content, revision } = data;

  return {
    content: content,
    revisionId: revision.id,
  };
};

export const useUpload = () =>
  useMutation({
    mutationFn: ({
      file,
      description,
      displayName,
      externalIdentifier,
    }: {
      file: File;
      description: string;
      displayName: string;
      externalIdentifier: string;
    }) => clientSDK.addModelLazy(file, [], description, "", externalIdentifier, displayName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: modelQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: ["modelsList"] });
    },
    meta: {
      skipGlobalInvalidation: true,
    },
  });

export const lookupOrFetchUser = async (userId: string) => {
  const orgUsers = await queryClient
    .ensureQueryData(userQueryOptions.all())
    .then((users) => users.map(userToInternalUser));
  let uploadedByUser = getUserName(orgUsers as OrgUser[], userId);
  if (uploadedByUser === UNKNOWN_USER_DISPLAY_NAME) {
    // The tenant list above is humans-only, so machine users (agents uploading job outputs)
    // always miss it. GET /users/{id} does return machine users, and
    // resolveUserDisplayNameOrUnknown falls through to machine_name for them instead of
    // requiring firstName/lastName.
    uploadedByUser = await queryClient
      .ensureQueryData(userQueryOptions.byId(userId))
      .then((user) => resolveUserDisplayNameOrUnknown(user))
      .catch(() => UNKNOWN_USER_DISPLAY_NAME);
  }
  return uploadedByUser;
};

export const useGetModels = ({ filter, enabled = true }: { filter: ListOptions; enabled?: boolean }) =>
  useQuery({
    queryKey: modelQueryKeys.list(filter),
    queryFn: () =>
      clientSDK
        .listModels(filter.page, filter.size, filter.createdBy, "active", filter.sort)
        .then(async (data) => {
          const list = data.items.map(async (model) => {
            const modelPropsData = [];
            const uploadedByUser = await lookupOrFetchUser(model.file.createdById!);
            const revision = model.file.currentRevision();

            modelPropsData.push({
              ...model,
              id: model.id,
              created: model.file.revisions[0].created,
              fileName: revision.name,
              mime: revision.mime,
              extension: revision.extension ? getConnectedFileExtension(revision.extension) : "",
              size: revision.size,
              description: revision.description,
              versionName: revision.versionName,
              externalIdentifier: revision.externalIdentifier,
              displayFileName:
                stripExtensionFromDisplayName(revision.displayName, revision.extension) ||
                getFileNameAndExtension(revision.name)?.name,
              uploaderName: uploadedByUser,
              upstreamRemote: model.file.upstreamRemote,
            });

            return modelPropsData;
          });

          const result = await Promise.all(list).then((response) => response.flat());

          return {
            items: result,
            total: data.total,
          };
        })
        .catch((error: Error) => {
          console.error("Error loading models", error);
          if (error.message.includes(String(HttpStatusCodeType.Forbidden))) {
            toast.error("Your session may have expired. Try refresh the page.");
          } else {
            toast.error("An error occurred while loading the models. Try refresh the page.");
          }
          return {
            items: [],
            total: 0,
          };
        }),
    enabled,
  });

export const useGetModelsList = (maxInfosecLevelOrder?: number, options?: { enabled?: boolean }) =>
  useInfiniteQuery({
    queryKey: ["modelsList", maxInfosecLevelOrder],
    queryFn: async ({ pageParam }) =>
      clientSDK.listModels(pageParam, 25, undefined, "active", "-created").then(async (data) => {
        const list = data.items.map(async (model) => {
          const modelPropsData = [];
          const revision = model.file.currentRevision();

          modelPropsData.push({
            ...model,
            id: model.id,
            created: model.file.revisions[0].created,
            fileName: revision.name,
            mime: revision.mime,
            extension: revision.extension ? getConnectedFileExtension(revision.extension) : "",
            size: revision.size,
            description: revision.description,
            versionName: revision.versionName,
            externalIdentifier: revision.externalIdentifier,
            displayFileName:
              stripExtensionFromDisplayName(revision.displayName, revision.extension) ||
              getFileNameAndExtension(revision.name)?.name,
            infosecLevel: model.file.infosecLevel,
          });

          return modelPropsData;
        });

        const result = await Promise.all(list).then((response) => response.flat());

        const filteredResult =
          maxInfosecLevelOrder !== undefined
            ? result.filter((item) => {
                const modelInfosecOrder = item.infosecLevel?.order;
                return modelInfosecOrder === undefined || modelInfosecOrder <= maxInfosecLevelOrder;
              })
            : result;

        return {
          items: filteredResult,
          page: data.page,
          pages: data.pages,
          total: data.total,
        };
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage?.items.length === 0) {
        return undefined;
      }
      return lastPageParam + 1;
    },
    getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
      if (firstPageParam <= 1) {
        return undefined;
      }
      return firstPageParam - 1;
    },
    enabled: options?.enabled ?? true,
    staleTime: DEFAULT_CACHE_TIME,
    placeholderData: keepPreviousData,
  });

export type ArtifactListType = Artifact & {
  displayFileName?: string | undefined;
  fileName: string;
  upstreamRemote?: UpstreamRemoteInfo | undefined;
};

export const useGetArtifacts = ({
  filter,
  listArtifactsOnlyState,
}: {
  filter: ListOptions;
  listArtifactsOnlyState: boolean;
}) =>
  useQuery({
    queryKey: artifactQueryKeys.list(filter),
    queryFn: () =>
      clientSDK
        .listArtifacts(filter.page, filter.size, filter.createdBy, "active", filter.sort)
        .then(async (data: PageArtifact) => {
          const list = data.items.map(async (artifact: Artifact) => {
            const revision = artifact.currentRevision();

            return {
              ...artifact,
              id: artifact.id,
              created: artifact.file.revisions[0].created,
              fileName: revision.name,
              mime: revision.mime,
              extension: revision.extension ? getConnectedFileExtension(revision.extension) : "",
              size: revision.size,
              description: revision.description,
              versionName: revision.versionName,
              externalIdentifier: revision.externalIdentifier,
              displayFileName:
                stripExtensionFromDisplayName(revision.displayName, revision.extension) ||
                getFileNameAndExtension(revision.name)?.name,
              upstreamRemote: artifact.file.upstreamRemote,
            };
          });

          const result = await Promise.all(list).then((response) => response.flat());

          return {
            items: result as unknown as ArtifactListType[],
            total: data.total,
          };
        })
        .catch((error: Error) => {
          console.error("Error loading artifacts", error);
          toast.error("An error occurred while loading the artifacts");
          return {
            items: [],
            total: 0,
          };
        }),
    ...{ enabled: listArtifactsOnlyState },
  });

export const useGetArtifactsList = (maxInfosecLevelOrder?: number, options?: { enabled?: boolean }) =>
  useInfiniteQuery({
    queryKey: ["artifactsList", maxInfosecLevelOrder],
    queryFn: async ({ pageParam }) =>
      clientSDK.listArtifacts(pageParam, 25, undefined, "active", "-created").then(async (data) => {
        const list = data.items.map(async (artifact: Artifact) => {
          const revision = artifact.currentRevision();

          return {
            ...artifact,
            id: artifact.id,
            created: artifact.file.revisions[0].created,
            fileName: revision.name,
            mime: revision.mime,
            extension: revision.extension ? getConnectedFileExtension(revision.extension) : "",
            size: revision.size,
            description: revision.description,
            versionName: revision.versionName,
            externalIdentifier: revision.externalIdentifier,
            displayFileName:
              stripExtensionFromDisplayName(revision.displayName, revision.extension) ||
              getFileNameAndExtension(revision.name)?.name,
            infosecLevel: artifact.file.infosecLevel,
          };
        });

        const result = await Promise.all(list).then((response) => response.flat());

        const filteredResult =
          maxInfosecLevelOrder !== undefined
            ? result.filter((item) => {
                const artifactInfosecOrder = item.infosecLevel?.order;
                return artifactInfosecOrder === undefined || artifactInfosecOrder <= maxInfosecLevelOrder;
              })
            : result;

        return {
          items: filteredResult,
          page: data.page,
          pages: data.pages,
          total: data.total,
        };
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage?.items.length === 0) {
        return undefined;
      }
      return lastPageParam + 1;
    },
    getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
      if (firstPageParam <= 1) {
        return undefined;
      }
      return firstPageParam - 1;
    },
    enabled: options?.enabled ?? true,
    staleTime: DEFAULT_CACHE_TIME,
    placeholderData: keepPreviousData,
  });

export const useGetModelAccessById = (fileId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: modelQueryKeys.accessList(fileId!),
    queryFn: () => clientSDK.listAccess(ResourceType.Model, fileId!),
    enabled: !!fileId && enabled,
    staleTime: DEFAULT_CACHE_TIME,
  });

export const useGetArtifactAccessById = (artifactId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: artifactQueryKeys.accessList(artifactId!),
    queryFn: () => clientSDK.listAccess(ResourceType.Artifact, artifactId!),
    enabled: !!artifactId && enabled,
    staleTime: DEFAULT_CACHE_TIME,
  });

/**
 * Access list for anything that is neither a model nor an artifact: a plain v3 file, a connected
 * file, a container. They are all one object type in the permission system -- `resource`, keyed by
 * resource id -- so one hook serves all three. Asking for them as a model returns nothing at best
 * and 404s at worst, because no `model` object of that id exists.
 */
export const useGetResourceAccessById = (resourceId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: resourceAccessQueryKeys.accessList(resourceId!),
    queryFn: () => clientSDK.listAccess(ResourceType.Resource, resourceId!),
    enabled: !!resourceId && enabled,
    staleTime: DEFAULT_CACHE_TIME,
  });

/**
 * Fetches model data with transformed revisions and properties.
 *
 * IMPORTANT: This hook intentionally does NOT share cache with getModelOptions/useModelRevision.
 *
 * History of cache deduplication attempts:
 * - dce16c96: Attempted to use `...getModelOptions(modelId)` with a `select` transform to share
 *   cache between useGetModel and useModelRevision, eliminating duplicate fetches.
 * - 211f5826: The select function created new object references on every call, causing infinite
 *   re-renders when combined with shared cache observers. Tried to fix using ensureQueryData
 *   with a separate queryKey (modelQueryKeys.itemFull).
 *
 * Both approaches were deemed too risky. The cache deduplication creates subtle issues with
 * React Query's referential equality checks. Until a stable solution is found, keep this hook
 * isolated with its own queryKey and direct API call.
 */
export const useGetModel = (modelId: string) =>
  useQuery({
    queryKey: modelQueryKeys.itemFull(modelId),
    enabled: !!modelId,
    queryFn: async () => {
      const model = await clientSDK.getModel(modelId);
      const revision = model.currentRevision();

      const modelFullRevisions = model.file.revisions.map((rev: FileRevision) => ({
        ...rev,
        label: rev.id,
        value: rev.id,
      }));

      const modelPropertiesWithDisplayName = {
        fileName: revision.name,
        mime: revision.mime,
        extension: revision.extension || "",
        size: revision.size,
        description: revision.description,
        versionName: revision.versionName,
        externalIdentifier: revision.externalIdentifier,
        displayFileName:
          stripExtensionFromDisplayName(revision.displayName, revision.extension) ||
          getFileNameAndExtension(revision.name)?.name,
      };

      return {
        model,
        file: {
          id: model.file.id,
          infosecLevel: model.file.infosecLevel,
        },
        modelFullRevisions: sortDateArrayByKeyAndOrder(
          modelFullRevisions,
          "created",
          "desc",
        ) as unknown as FileRevision[],
        modelProperties: modelPropertiesWithDisplayName,
        modelTags: model.controlTags?.map((tag) => ({
          ...tag,
          color: tag.displayColor,
          label: tag.name,
          value: tag.id,
        })) as LabeledControlTag[],
      };
    },
  });

export const getFileRevisionPropertiesByShaSalt = async (sha: string, salt: string) => {
  return await clientSDK.readProperties(sha, salt);
};

export const getFileRevisionPropertiesByRevision = (revision: FileRevision): FileProperties => {
  if (!revision.name || !revision.extension || !revision.size) {
    throw new Error(`Revision properties for ${revision.id} not found`);
  }

  return {
    fileName: revision.name,
    mime: revision.mime,
    extension: revision.extension,
    size: revision.size,
    description: revision.description,
    versionName: revision.versionName,
    externalIdentifier: revision.externalIdentifier,
    displayName: revision.displayName,
  };
};

export async function getFileRevisionContentByShaSalt(sha: string, salt: string) {
  return await clientSDK.readContent(sha, salt);
}

export interface UploadModelVersionPayloadType {
  modelId?: string;
  file: File;
  description?: string;
  externalIdentifier?: string;
  versionName?: string;
}

export const useUploadModelVersion = (options?: {
  onSuccess?: (data: IstariModel) => void;
  onError?: (error: unknown) => void;
}) =>
  useMutation({
    mutationFn: (data: UploadModelVersionPayloadType) =>
      clientSDK.updateModelLazy(
        data.modelId!,
        data.file,
        [],
        data.description,
        undefined,
        data.externalIdentifier,
        data.versionName,
      ),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: modelQueryKeys.item(variables.modelId!) });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
    meta: {
      skipGlobalInvalidation: true,
    },
  });

export const downloadFile = async (
  modelContentBytes: Uint8Array | number[],
  name: string | undefined,
  extension: string | undefined,
) => {
  // An extension is optional here, not a precondition. A connected resource often has none -- a
  // model element is not a file at its source, so the registry states no extension for it rather
  // than inventing one -- and requiring one failed those downloads before a byte was written,
  // with a generic toast and nothing in the server logs to explain it. What the suffix should be
  // is `revisionDownloadName`'s question, answered before the content is ever fetched.
  if (!modelContentBytes || !name) {
    toast.error("An error occurred while downloading the file.");
    return;
  }
  const filename = extension ? `${name}.${extension}` : name;
  const blob = new Blob([new Uint8Array(modelContentBytes)]);
  try {
    await downloadBlob(extension ? new Blob([blob], { type: extension }) : blob, filename);
    toast.success(`${filename} has been downloaded successfully.`);
  } catch (error) {
    console.error("Download error:", error);
    toast.error("An error occurred while downloading the file.");
  }
};

export const useShareFile = () =>
  useMutation({
    mutationFn: async (data: {
      entityId: string | undefined;
      accessGrants: {
        action: AccessActionType;
        userId: string | undefined;
        accessRelationship: AccessRelationship;
      }[];
      resourceType: ResourceType.Model | ResourceType.Artifact | ResourceType.Resource;
    }) => {
      const createGrants = data.accessGrants.filter((item) => item.action === AccessActionType.create);
      const updateGrants = data.accessGrants.filter((item) => item.action === AccessActionType.update);
      const removeGrants = data.accessGrants.filter((item) => item.action === AccessActionType.remove);

      const promises: Promise<unknown>[] = [];

      // Handle bulk create operations
      if (createGrants.length > 0) {
        // Separate org users from cross-tenant emails
        const orgUserGrants = createGrants.filter((item) => item.userId);
        const crossTenantEmailGrants = createGrants.filter((item) => !item.userId);

        // Bulk create for org users
        if (orgUserGrants.length > 0) {
          const accessRelationships = orgUserGrants.map((item) => item.accessRelationship);
          promises.push(clientSDK.bulkCreateAccess(accessRelationships));
        }

        // Individual calls for cross-tenant emails (no bulk endpoint available)
        crossTenantEmailGrants.forEach((item) => {
          promises.push(
            clientSDK.createAccessByEmailForOtherTenants(
              item.accessRelationship.subjectType,
              item.accessRelationship.subjectId,
              data.resourceType,
              item.accessRelationship.resourceId,
              item.accessRelationship.relation,
            ),
          );
        });
      }

      // Handle update operations individually
      updateGrants.forEach((item) => {
        promises.push(
          clientSDK.updateAccess(
            "user",
            item.accessRelationship.subjectId,
            data.resourceType,
            data.entityId!,
            item.accessRelationship,
          ),
        );
      });

      // Handle remove operations individually
      removeGrants.forEach((item) => {
        promises.push(
          clientSDK.removeAccess("user", item.accessRelationship.subjectId, data.resourceType, data.entityId!),
        );
      });

      await Promise.all(promises);
    },
    onSuccess: (_data, variables) => {
      const id = variables.entityId!;
      if (variables.resourceType === ResourceType.Model) {
        queryClient.invalidateQueries({ queryKey: modelQueryKeys.accessList(id) });
      } else if (variables.resourceType === ResourceType.Resource) {
        queryClient.invalidateQueries({ queryKey: resourceAccessQueryKeys.accessList(id) });
      } else {
        queryClient.invalidateQueries({ queryKey: artifactQueryKeys.accessList(id) });
      }
    },
    meta: {
      skipGlobalInvalidation: true,
    },
  });

export type ExecuteFunctionPayload = {
  functionName: string;
  modelId: string;
  operatingSystem?: string;
  parametersFile: File;
  toolKey: string;
  toolVersion: string;
  sourcesInfo: Source[] | undefined;
  assignedAgentId?: string | undefined;
  assignedAgentPoolId?: string | undefined;
  // Bind a stored connection to each auth_info input. When present, the job is submitted via
  // createModelJob (the convenience addJobLazy can't carry auth bindings).
  authBindings?: NewCredentialBinding[];
};

export const useExecuteFunction = () =>
  useMutation({
    mutationFn: async (data: ExecuteFunctionPayload) => {
      // convert the string to the corresponding enum key
      const mappedOSKey = (Object.keys(OS) as (keyof typeof OS)[]).find((key) => OS[key] === data.operatingSystem);
      const mappedOS = mappedOSKey ? OS[mappedOSKey] : undefined;

      // A bound connection delivers the secret to the auth_info input(s): the registry mints and
      // delivers it per-agent at claim, so no secret is handled client-side. addJobLazy can't carry
      // bindings, so these jobs go through createModelJob instead.
      if (data.authBindings && data.authBindings.length > 0) {
        // The model input comes from modelId and the params file is the job's own revision, so don't
        // pass the params file itself as a source (that would double-bind the model). The user's
        // selected sources still ride on the revision, matching the addJobLazy path below.
        const revision = await clientSDK.createRevision({ file: data.parametersFile, sources: data.sourcesInfo });
        return clientSDK.createModelJob(
          data.modelId,
          data.functionName,
          new NewJob(revision, data.authBindings),
          undefined,
          data.toolKey,
          data.toolVersion,
          mappedOS,
          data.assignedAgentId,
          data.assignedAgentPoolId,
        );
      }

      return clientSDK.addJobLazy(
        data.modelId,
        data.parametersFile,
        data.functionName,
        data.toolKey,
        data.toolVersion,
        mappedOS,
        data.assignedAgentId,
        data.assignedAgentPoolId,
        undefined,
        data.sourcesInfo,
      );
    },
    onSuccess: (_data, variables) => queryClient.refetchQueries({ queryKey: modelQueryKeys.item(variables.modelId) }),
    onError: () => {
      toast.error("An error occurred while starting a model extraction job.");
    },
  });

export const useGetAllArtifactsProperties = (artifacts: FileRevisionWithArtifactId[] | undefined) =>
  useQuery({
    queryKey: modelQueryKeys.artifactsProperties(artifacts!),
    queryFn: async () => {
      if (artifacts) {
        const results = await Promise.all(
          artifacts.map(async (artifact) => ({
            ...artifact,
            displayFileName:
              stripExtensionFromDisplayName(artifact.displayFileName, artifact.extension) ||
              getFileNameAndExtension(artifact.name)?.name,
            id: artifact.id,
          })),
        );

        return {
          items: results,
          total: artifacts?.length,
        };
      }
      return {
        items: [],
        total: 0,
      };
    },
  });

export const useGetArtifact = (artifactId: string) =>
  useQuery({
    queryKey: artifactQueryKeys.item(artifactId),
    queryFn: () =>
      clientSDK
        .getArtifact(artifactId)
        .then(async (artifact: Artifact) => {
          const revision = artifact.currentRevision();
          const artifactAccessList = await clientSDK.listAccess(ResourceType.Artifact, artifactId);

          const revs = artifact.file.revisions.map(async (rev: FileRevision) => {
            const revProp = getFileRevisionPropertiesByRevision(rev);

            return { ...rev, label: rev.id, value: rev.id, ...revProp };
          });
          const artifactRevisions = await Promise.all(revs);

          return {
            artifact,
            artifactAccessList,
            displayFileName:
              stripExtensionFromDisplayName(revision.displayName, revision.extension) ||
              getFileNameAndExtension(revision.name)?.name,
            extension: getConnectedFileExtension(revision.extension!) || "",
            artifactTags: artifact.controlTags?.map((tag) => ({
              ...tag,
              color: tag.displayColor,
              label: tag.name,
              value: tag.id,
            })) as LabeledControlTag[],
            artifactRevisions,
          };
        })
        .catch((error: Error) => {
          console.error("Error loading artifact", error);
          if (
            error.message.includes(String(HttpStatusCodeType.Forbidden)) ||
            error.message.includes(String(HttpStatusCodeType.NotFound))
          ) {
            throw new Error("This artifact is not found or you do not have permission to view this artifact.");
          } else {
            throw new Error("An error occurred while loading the artifact", error);
          }
        }),
    enabled: !!artifactId,
  });

export const useRenameModel = (options?: { onSuccess?: () => void; onError?: (error: unknown) => void }) =>
  useMutation({
    mutationFn: (data: { file: IstariFile; name: string }) => clientSDK.updateFilePropertiesLazy(data.file, data.name),
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });

export const useListModelJobs = ({
  modelId,
  filter,
  enabled = true,
  poll = false,
}: {
  modelId: string;
  filter: TaskListOptions;
  enabled?: boolean;
  poll?: boolean;
}) =>
  useQuery({
    queryKey: modelQueryKeys.tasks(modelId, { ...filter }),
    queryFn: () =>
      clientSDK
        .listJobs(
          modelId,
          filter.statusName ?? null,
          filter.allUsers,
          null,
          null,
          filter.page,
          filter.size,
          "active",
          filter.sort,
        )
        .catch((error: Error) => {
          console.error("Error loading model jobs", error);
          toast.error("An error occurred while loading the model jobs");
          return undefined;
        }),
    staleTime: QUERY_STALE_TIME_MS_30S,
    enabled: enabled && !!modelId,
    refetchInterval: poll ? adaptiveJobPageRefetchInterval : false,
    placeholderData: keepPreviousData,
  });

function getSizeInBytes(size: string | undefined, unit: string | undefined) {
  if (!size) {
    return undefined;
  }
  switch (unit) {
    case "bytes":
      return [parseInt(size, 10)];
    case "KB":
      return [parseInt(size, 10) * 1000];
    case "MB":
      return [parseInt(size, 10) * 1000 * 1000];
    case "GB":
      return [parseInt(size, 10) * 1000 * 1000 * 1000];
    default:
      return [parseInt(size, 10)];
  }
}

const isUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export type SearchResultsType = PageResourceSearchItem | { errorSearching: boolean } | undefined;
export type ResourceSearchItemWithUploader = ResourceSearchItem & {
  uploaderName: string;
};

export type SearchQueryType = {
  archiveStatus?: ArchiveStatusName[] | null | undefined;
  createdById?: string[] | undefined;
  createdTimestamp?: string | null | undefined;
  description?: string | null;
  externalIdentifier?: string[] | undefined;
  fileName?: string | null | undefined;
  fileSize?: string;
  fileSizeUnit?: string;
  typeName?: string[] | null | undefined;
  updatedById?: string[] | undefined;
  updatedTimestamp?: string | null | undefined;
  generalSearchTerm?: string;
  pageIndex?: number | undefined;
  pageSize?: number | undefined;
  infosecLevelId?: string[] | undefined;
  sort?: string | null;
};

// eslint-disable-next-line complexity -- TODO fix later
export async function searchResources(searchQuery: SearchQueryType) {
  /*
        page?: number,
        size?: number,
        id?: Array<string> | null,
        fileName?: Array<string> | null,
        typeName?: Array<ResourceType> | null,
        description?: Array<string> | null,
        versionName?: Array<string> | null,
        externalIdentifier?: Array<string> | null,
        displayName?: Array<string> | null,
        mimeType?: Array<string> | null,
        fileSize?: Array<number> | null,
        createdTimestamp?: Array<Date> | null,
        updatedTimestamp?: Array<Date> | null,
        generalSearchTerm?: string,
        createdById?: Array<string> | null,
        updatedById?: Array<string> | null,
        archiveStatus?: Array<ArchiveStatusName> | null,
        infosecLevelId?: Array<string> | null,
        sort?: string | null,
  */
  // Convert createdTimestamp and updatedTimestamp to Date[] | null | undefined
  const createdTimestampArray: Date[] | null = searchQuery.createdTimestamp
    ? [new Date(searchQuery.createdTimestamp)]
    : null;
  const updatedTimestampArray: Date[] | null = searchQuery.updatedTimestamp
    ? [new Date(searchQuery.updatedTimestamp)]
    : null;

  // Determine if generalSearchTerm is a UUID or a name.
  // TODO: ask users to prefix their input with field type
  let searchById: string[] | null = null;
  let searchByNameAndDisplayName: string[] | null = null;

  if (searchQuery.generalSearchTerm) {
    if (isUUID(searchQuery.generalSearchTerm)) {
      searchById = [searchQuery.generalSearchTerm];
    } else {
      searchByNameAndDisplayName = [`%${searchQuery.generalSearchTerm}%`];
    }
  }

  if (!searchByNameAndDisplayName) {
    return clientSDK
      .listResources(
        searchQuery.pageIndex || 1,
        searchQuery.pageSize || 10,
        searchById,
        searchQuery.fileName ? [searchQuery.fileName] : null,
        searchQuery.typeName as ClientResourceType[] | null,
        searchQuery.description ? [searchQuery.description] : null,
        null,
        searchQuery.externalIdentifier,
        null,
        null,
        getSizeInBytes(searchQuery.fileSize, searchQuery.fileSizeUnit),
        createdTimestampArray,
        updatedTimestampArray,
        searchQuery.createdById,
        searchQuery.updatedById,
        searchQuery.archiveStatus,
        undefined,
        searchQuery.infosecLevelId,
        searchQuery.sort,
      )
      .then(async (data) => {
        const list = data.items.map(async (item: ResourceSearchItem) => {
          const displayName = item.displayName || getFileNameAndExtension(item.name)?.name;
          const uploadedByUser = await lookupOrFetchUser(item.createdById!);

          return {
            ...item,
            displayName,
            extension: getConnectedFileExtension(item.extension!),
            uploaderName: uploadedByUser,
          };
        }) as unknown as ResourceSearchItemWithUploader[];

        const normalizedItems = await Promise.all(list).then((response) => response.flat());
        return {
          ...data,
          items: normalizedItems,
        };
      })
      .catch((error: Error) => {
        console.error("Error searching resources", error);
        return {
          errorSearching: true,
        };
      });
  }

  // If a non-UUID general search term is passed, we need to handle this specially by searching by both file name and display name.
  try {
    const fileNamePromise = clientSDK.listResources(
      searchQuery.pageIndex || 1,
      searchQuery.pageSize || 25,
      null,
      searchByNameAndDisplayName,
      searchQuery.typeName as ClientResourceType[] | null,
      searchQuery.description ? [searchQuery.description] : null,
      null,
      searchQuery.externalIdentifier,
      null,
      null,
      getSizeInBytes(searchQuery.fileSize, searchQuery.fileSizeUnit),
      createdTimestampArray,
      updatedTimestampArray,
      searchQuery.createdById,
      searchQuery.updatedById,
      searchQuery.archiveStatus,
      undefined,
      searchQuery.infosecLevelId,
      searchQuery.sort,
    );

    const displayNamePromise = clientSDK.listResources(
      searchQuery.pageIndex || 1,
      searchQuery.pageSize || 25,
      null,
      null,
      searchQuery.typeName as ClientResourceType[] | null,
      searchQuery.description ? [searchQuery.description] : null,
      null,
      searchQuery.externalIdentifier,
      searchByNameAndDisplayName,
      null,
      getSizeInBytes(searchQuery.fileSize, searchQuery.fileSizeUnit),
      createdTimestampArray,
      updatedTimestampArray,
      searchQuery.createdById,
      searchQuery.updatedById,
      searchQuery.archiveStatus,
      undefined,
      searchQuery.infosecLevelId,
      searchQuery.sort,
    );

    // Combine and dedupe the results before returning
    const [fileNameResults, displayNameResults] = await Promise.all([fileNamePromise, displayNamePromise]);
    const allResults = [...fileNameResults.items, ...displayNameResults.items];
    const uniqueResults = allResults.filter((item, index, array) => array.findIndex((i) => i.id === item.id) === index);
    const list = uniqueResults.map(async (item: ResourceSearchItem) => {
      const displayName = item.displayName || getFileNameAndExtension(item.name)?.name;
      const uploadedByUser = await lookupOrFetchUser(item.createdById!);

      return {
        ...item,
        displayName,
        extension: getConnectedFileExtension(item.extension!),
        uploaderName: uploadedByUser,
      };
    }) as unknown as ResourceSearchItemWithUploader[];

    const normalizedItems = await Promise.all(list).then((response) => response.flat());
    return {
      items: normalizedItems,
      total: (fileNameResults.total || 0) + (displayNameResults.total || 0), // This will be an overcount if the same file is returned from both calls. There is no graceful way to get totals here without a full scan.
      page: searchQuery.pageIndex || 1,
      pages: Math.ceil(((fileNameResults.total || 0) + (displayNameResults.total || 0)) / (searchQuery.pageSize || 10)), // May also be an overcount
      size: searchQuery.pageSize || 10,
    };
  } catch (error: unknown) {
    console.error("Error searching resources", error);
    return {
      errorSearching: true,
    };
  }
}

export const useSearchArtifacts = (generalSearchTerm?: string, infosecLevelMaxOrder?: number) =>
  useQuery({
    queryKey: ["searchArtifacts", generalSearchTerm, infosecLevelMaxOrder],
    queryFn: async () => {
      let infosecLevelId: string[] | undefined;

      if (infosecLevelMaxOrder !== undefined) {
        const infosecLevels = await queryClient.ensureQueryData({
          queryKey: infosecLevelsKeys.all,
          queryFn: () => clientSDK.listInfosecLevels(),
        });

        infosecLevelId = infosecLevels.filter((level) => level.order <= infosecLevelMaxOrder).map((level) => level.id);
      }

      const searchQuery: SearchQueryType = {
        pageIndex: 1,
        pageSize: 50,
        typeName: [ClientResourceType.Artifact],
        archiveStatus: ["Active"],
        sort: "-created",
        generalSearchTerm,
        infosecLevelId,
      };

      return searchResources(searchQuery);
    },
    enabled: !!generalSearchTerm && generalSearchTerm.length >= 2,
    staleTime: DEFAULT_CACHE_TIME,
  });

export const useSearchModels = (generalSearchTerm?: string, infosecLevelMaxOrder?: number) =>
  useQuery({
    queryKey: ["searchModels", generalSearchTerm, infosecLevelMaxOrder],
    queryFn: async () => {
      let infosecLevelId: string[] | undefined;

      if (infosecLevelMaxOrder !== undefined) {
        const infosecLevels = await queryClient.ensureQueryData({
          queryKey: infosecLevelsKeys.all,
          queryFn: () => clientSDK.listInfosecLevels(),
        });

        infosecLevelId = infosecLevels.filter((level) => level.order <= infosecLevelMaxOrder).map((level) => level.id);
      }

      const searchQuery: SearchQueryType = {
        pageIndex: 1,
        pageSize: 50,
        typeName: [ClientResourceType.Model],
        archiveStatus: ["Active"],
        sort: "-created",
        generalSearchTerm,
        infosecLevelId,
      };

      return searchResources(searchQuery);
    },
    enabled: !!generalSearchTerm && generalSearchTerm.length >= 2,
    staleTime: DEFAULT_CACHE_TIME,
  });

export const fetchFileByRevisionId = (revisionId: string | undefined) =>
  clientSDK
    .getFileByRevisionId(revisionId!)
    .then((response) => response)
    .catch(() => {
      throw new Error("An error occurred while loading the file revision.");
    });

export const useGetFile = (fileId?: string, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: ["file", fileId],
    queryFn: () =>
      clientSDK
        .getFile(fileId!)
        .then((response) => response)
        .catch(() => {
          throw new Error("An error occurred while loading the file.");
        }),
    enabled: options?.enabled !== undefined ? !!fileId && options.enabled : !!fileId,
    staleTime: DEFAULT_CACHE_TIME,
  });

export const useGetRevision = (revisionId?: string, options?: { enabled?: boolean }) =>
  useQuery({
    ...fileRevisionQueryOptions(revisionId ?? ""),
    enabled: options?.enabled !== undefined ? !!revisionId && options.enabled : !!revisionId,
  });
